import { Controller } from "@hotwired/stimulus"
import { debounce } from "../application"

export default class extends Controller {
    static targets = ["emptyIcon", "filledIcon"]
    static values = {
        productId: Number,
        isGlobal: { type: Boolean, default: false }
    }

    STORAGE_KEY = 'savedProductIds'
    savedProductIds = new Set()

    // Simple throttle implementation
    throttle(func, limit) {
        let inThrottle
        return function (...args) {
            if (!inThrottle) {
                func.apply(this, args)
                inThrottle = true
                setTimeout(() => inThrottle = false, limit)
            }
        }
    }

    isAuthenticated() {
        const metaTag = document.querySelector('meta[name="turbo:authenticated"]')
        return metaTag && metaTag.content === 'true'
    }

    connect() {
        // Only the global, authenticated instance fetches and broadcasts. Track
        // that decision so disconnect() tears down exactly what connect() set up.
        this.isGlobalFetcher = this.isGlobalValue && this.isAuthenticated()

        if (this.isGlobalFetcher) {
            // Load from localStorage first
            this.loadFromLocalStorage()
            this.updateAllBookmarkIcons()

            // Then fetch fresh data using throttled function
            this.throttledFetchSavedProducts = this.throttle(this.fetchSavedProducts.bind(this), 1000)

            // Refresh on navigation and after save/unsave submissions. We deliberately
            // do NOT listen for turbo:frame-render: the saved set can't change from
            // rendering frames (e.g. infinite scroll), and newly rendered icons already
            // self-initialize from this controller on their own connect().
            this.handleTurboLoad = (event) => {
                // First update from localStorage
                this.loadFromLocalStorage()
                this.updateAllBookmarkIcons()
                // Then fetch fresh data
                this.throttledFetchSavedProducts()
            }
            document.addEventListener('turbo:load', this.handleTurboLoad)
            document.addEventListener('turbo:submit-end', this.handleTurboLoad)

            // Add listener for native modal state changes
            this.debouncedHandleModalStateChange = debounce(() => {
                if (this.isAuthenticated()) {
                    this.fetchSavedProducts()
                }
            }, 1000)
            document.addEventListener('hotwire-native::modal-dismissed', this.debouncedHandleModalStateChange)

            // Initial fetch
            this.throttledFetchSavedProducts()
        } else {
            // Individual bookmark icon - find global controller and listen for updates
            const globalElement = document.querySelector('[data-controller="bookmark-icon"][data-bookmark-icon-is-global-value="true"]')
            if (globalElement) {
                const globalController = this.application.getControllerForElementAndIdentifier(globalElement, 'bookmark-icon')
                if (globalController) {
                    this.handleUpdate({ detail: { savedProductIds: Array.from(globalController.savedProductIds) } })
                }
            }
            // Keep a single bound reference so disconnect() can actually remove it.
            this.boundHandleUpdate = this.handleUpdate.bind(this)
            document.addEventListener('savedProductsUpdate', this.boundHandleUpdate)
        }
    }

    disconnect() {
        if (this.isGlobalFetcher) {
            document.removeEventListener('turbo:load', this.handleTurboLoad)
            document.removeEventListener('turbo:submit-end', this.handleTurboLoad)
            document.removeEventListener('hotwire-native::modal-dismissed', this.debouncedHandleModalStateChange)
        } else {
            document.removeEventListener('savedProductsUpdate', this.boundHandleUpdate)
        }
    }

    loadFromLocalStorage() {
        const savedIds = localStorage.getItem(this.STORAGE_KEY)
        if (savedIds) {
            this.savedProductIds = new Set(JSON.parse(savedIds))
        }
    }

    async fetchSavedProducts() {
        try {
            const response = await fetch('/api/user/saved-products')
            if (!response.ok) return

            const data = await response.json()
            this.savedProductIds = new Set(data.product_ids)

            // Save to localStorage
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(Array.from(this.savedProductIds)))

            this.updateAllBookmarkIcons()
        } catch (error) {
            console.error('Error fetching saved products:', error)
        }
    }

    updateAllBookmarkIcons() {
        const event = new CustomEvent('savedProductsUpdate', {
            detail: { savedProductIds: Array.from(this.savedProductIds) },
            bubbles: true
        })
        document.dispatchEvent(event)
    }

    handleUpdate(event) {
        if (this.isGlobalValue) return

        const savedProductIds = new Set(event.detail.savedProductIds)
        const isSaved = savedProductIds.has(this.productIdValue)

        this.emptyIconTarget.classList.toggle('hidden', isSaved)
        this.filledIconTarget.classList.toggle('hidden', !isSaved)
    }
} 